/**
 * Holds the window's current selection (RFC 0297 §4e).
 *
 * Mounted at the shell because a selection made in a document pane is read by
 * the bar above a conversation, which is a different pane.
 */

import { type ReactNode, useCallback, useMemo, useState } from 'react'
import {
  type Selection,
  SelectionContext,
} from '../../services/sessions/selection'

export function SelectionProvider({ children }: { children: ReactNode }) {
  const [selection, setSelection] = useState<Selection | null>(null)

  const select = useCallback((next: Selection | null) => {
    // NULL DOES NOT CLEAR. `selectionchange` fires on every click, including the
    // one into the composer, so honouring null here would drop the passage at
    // the moment a person went to type about it. Only `clear` clears.
    if (next === null) return
    setSelection((prev) =>
      prev &&
      prev.documentId === next.documentId &&
      prev.label === next.label &&
      prev.ref === next.ref
        ? // Same passage: keep the object, so a consumer memoised on it does
          // not re-render for every `selectionchange` a drag emits.
          prev
        : next,
    )
  }, [])

  const clear = useCallback(() => setSelection(null), [])

  const value = useMemo(
    () => ({ selection, select, clear }),
    [selection, select, clear],
  )

  return (
    <SelectionContext.Provider value={value}>
      {children}
    </SelectionContext.Provider>
  )
}
